import { useEffect, useState } from "react";
import { Card, Table, Input, Button, message } from "antd";
import { SearchOutlined, FilterOutlined, DownloadOutlined } from "@ant-design/icons";
import { Activity, IndianRupee, Coins, Clock } from "lucide-react";
import reapi from "../../../api/reapi";

export default function AdminOptimizations() {
  const [loading, setLoading] = useState(false);
  const [optimizations, setOptimizations] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState("");

  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const fetchOptimizations = async () => {
    try {
      setLoading(true);

      const skip = (page - 1) * limit;
      const token = localStorage.getItem("meeshoadminToken");

      const res = await reapi.get(
        `/adm/optimizations?skip=${skip}&limit=${limit}&search=${search}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setOptimizations(res.data.data.optimizations || []);
      setStats(res.data.data.stats || {});
      setTotal(res.data.data.total || 0);
    } catch (error) {
      message.error(error?.response?.data?.message || "Optimizations load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptimizations();
  }, [page, limit]);

  const columns = [
    {
      title: "#",
      width: 60,
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "User",
      dataIndex: "userName",
      width: 180,
      render: (v) => <b>{v || "User"}</b>,
    },
    {
      title: "Category",
      dataIndex: "category",
      width: 150,
    },
    {
      title: "Original Shipping",
      dataIndex: "originalshipping",
      width: 150,
      render: (v) => `₹${v || 0}`,
    },
    {
      title: "Optimized Shipping",
      dataIndex: "optimizedshipping",
      width: 160,
      render: (v) => <b className="text-green-600">₹{v || 0}</b>,
    },
    {
      title: "Savings",
      dataIndex: "savings",
      width: 120,
      render: (v) => <b className="text-green-600">₹{v || 0}</b>,
    },
    {
      title: "Credits Used",
      dataIndex: "credits",
      width: 130,
    },
    {
      title: "Variations",
      dataIndex: "variationsChecked",
      width: 120,
    },
    {
      title: "Time",
      dataIndex: "timetake",
      width: 100,
      render: (v) => `${v || 0}s`,
    },
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      width: 200,
      render: (date) =>
        new Date(date).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      title: "Actions",
      width: 100,
      render: (_, record) => (
        <Button onClick={() => window.open(record.imgurl, "_blank")}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-7">
        <h1 className="text-4xl font-extrabold m-0">Optimizations</h1>
        <p className="text-slate-500 mt-2">
          View all image optimizations and shipping savings.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <Input
          size="large"
          placeholder="Search by category..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onPressEnter={() => {
            setPage(1);
            fetchOptimizations();
          }}
          className="rounded-xl"
        />

        <Button size="large" icon={<FilterOutlined />} className="h-12 px-8">
          Filter
        </Button>

        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          className="h-12 px-8 bg-purple-600"
        >
          Export
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <StatCard
          icon={<Activity />}
          title="Total Optimizations"
          value={stats.totalOptimizations || 0}
          color="purple"
        />

        <StatCard
          icon={<IndianRupee />}
          title="Total Savings"
          value={`₹${stats.totalSavings || 0}`}
          color="green"
        />

        <StatCard
          icon={<Coins />}
          title="Credits Used"
          value={stats.totalCreditsUsed || 0}
          color="orange"
        />

        <StatCard
          icon={<Clock />}
          title="Avg. Processing Time"
          value={`${stats.avgProcessingTime || 0}s`}
          color="red"
        />
      </div>

      <Card className="rounded-2xl shadow-sm overflow-hidden">
        <div className="w-full overflow-x-auto">
          <Table
            loading={loading}
            columns={columns}
            dataSource={optimizations}
            rowKey="_id"
            pagination={{
              current: page,
              pageSize: limit,
              total,
              showSizeChanger: true,
              onChange: (p, ps) => {
                setPage(p);
                setLimit(ps);
              },
            }}
            scroll={{ x: 1400 }}
          />
        </div>
      </Card>
    </div>
  );
}

function StatCard({ icon, title, value, color }) {
  const colors = {
    purple: "bg-purple-100 text-purple-600",
    green: "bg-green-100 text-green-600",
    orange: "bg-orange-100 text-orange-600",
    red: "bg-red-100 text-red-600",
  };

  return (
    <Card className="rounded-2xl shadow-sm">
      <div className="flex items-center gap-5">
        <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>

        <div>
          <p className="text-slate-500 m-0">{title}</p>
          <h2 className="text-3xl font-extrabold m-0 mt-2">{value}</h2>
          <p className="text-green-600 m-0 mt-2">↑ 16.3%</p>
        </div>
      </div>
    </Card>
  );
}